import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Box, Lock } from "lucide-react";
import { api } from "../lib/api";
import { ThemeToggle } from "../components/brand/Experience";
import { ApprovalBar, GlbUpload, PipelineCard, QualityPanel } from "../components/three/Bao3DPanels";
import { ViewerPanel, VIEW_FR } from "../components/three/ViewerPanel";
import { propFor } from "../components/three/props3d";

const refFor = (refs, view) => {
  const want = { front: "_front", left: "_side", right: "_side", back: "_back", threeQuarter: "_threequarter" }[view];
  return refs.find((r) => r.includes(want)) || refs[0];
};

const CastTabs = ({ list, value, onChange }) => (
  <div className="flex max-w-full snap-x gap-2 overflow-x-auto pb-1" role="tablist" data-testid="gallery3d-tabs">
    {list.map((c) => (
      <button key={c.character} role="tab" aria-selected={value === c.character} data-testid={`gallery3d-tab-${c.character}`} onClick={() => onChange(c.character)}
        className={`flex shrink-0 snap-start items-center gap-2 rounded-2xl border-2 p-1.5 pr-3 text-left transition-colors ${value === c.character ? "border-[#17A2A2] bg-[#0A2540] text-white" : "border-transparent bg-muted"}`}>
        <img decoding="async" src={c.reference_views[0]} alt="" className="h-12 w-9 rounded-xl bg-[#EEF3F1] object-cover object-top" />
        <span><span className="block text-sm font-extrabold leading-tight">{c.name}</span><span className="flex items-center gap-1 text-[10px] opacity-75">{c.locked && <Lock className="h-3 w-3" />}{c.approval ? "Validé" : c.ready_job ? "Modèle prêt" : c.locked ? "Après Bao" : "À générer"}</span></span>
      </button>
    ))}
  </div>
);

export const versionLabel = (j, all) => `V${all.length - all.findIndex((x) => x.id === j.id)} · ${j.variant_label || (j.source === "upload" ? "Import artiste" : "Original")}`;

const VersionPicker = ({ jobs, value, approvedId, onChange }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="version-picker">
    {jobs.map((j) => (
      <button key={j.id} role="radio" aria-checked={value === j.id} data-testid={`version-${j.id}`} onClick={() => onChange(j.id)}
        className={`h-10 rounded-full px-4 text-sm font-bold ${value === j.id ? "bg-[#0A2540] text-white" : "bg-muted"}`}>
        {versionLabel(j, jobs)}{approvedId === j.id ? " ✓ validée" : ""}
      </button>
    ))}
  </div>
);

export default function Bao3D() {
  const nav = useNavigate();
  const [qs, setQs] = useSearchParams();
  const who = qs.get("c") || "bao", testGlb = qs.get("test_glb");
  const [list, setList] = useState([]);
  const [view, setView] = useState("threeQuarter");
  const [stats, setStats] = useState(null);
  const [pick, setPick] = useState(null);
  const st = list.find((c) => c.character === who);

  const load = useCallback(() => api.get("/characters3d").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStats(null); setPick(null); }, [who]);
  const running = ["QUEUED", "RUNNING"].includes(st?.latest_job?.status) || ["QUEUED", "RUNNING"].includes(st?.prop?.status);
  useEffect(() => { if (!running) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);

  const ready = st?.ready_jobs?.find((j) => j.id === pick) || st?.ready_job, f = ready?.files || {}, file = f.animations || f.rigged || f.generated || f.model;
  const url = testGlb || (file ? `${process.env.REACT_APP_BACKEND_URL}/api/characters3d/files/${file}` : null);
  const pfid = !testGlb && propFor(st, ready), prop = useMemo(() => (pfid ? { fid: pfid, character: who, label: st?.prop?.label, idle: Math.max(0, (ready?.clips || []).findIndex((c) => c.key === "idle")) } : null), [pfid, who, st?.prop?.label, ready?.clips]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground" data-testid="bao3d-page">
      <header className="safe-top flex items-center justify-between px-4 py-4 sm:px-8">
        <button data-testid="bao3d-back" onClick={() => nav("/bao")} className="flex h-11 items-center gap-2 rounded-full bg-muted px-4 text-sm font-bold"><ArrowLeft className="h-4 w-4" /> Studio</button>
        <ThemeToggle />
      </header>
      <main className="mx-auto min-w-0 max-w-7xl space-y-6 px-4 pb-16 sm:px-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-strong">Galerie des personnages 3D · {who === "bao" ? "Phase 1 · Character Master" : "Phase 2"}</p>
          <h1 className="mt-1 font-display text-4xl font-semibold sm:text-5xl" data-testid="gallery3d-title">{st?.name || "…"}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{st?.role}. Visualiseur 360° de vrais modèles 3D (GLB texturés, squelette, animations). Les images 2D sont toujours étiquetées comme références, jamais présentées comme de la 3D.</p>
        </div>
        <CastTabs list={list} value={who} onChange={(c) => setQs({ c })} />
        {st?.locked && <p className="flex items-center gap-2 rounded-2xl bg-[#FFF4DC] p-3 text-sm text-[#5C3B00]" data-testid="gallery3d-locked"><Lock className="h-4 w-4" /> La génération de {st.name} se débloque après la validation du modèle 3D de Bao. Tu peux déjà importer un GLB d'artiste.</p>}

        <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div className="min-w-0 space-y-4">
            {st?.ready_jobs?.length > 1 && !testGlb && <VersionPicker jobs={st.ready_jobs} value={ready.id} approvedId={st.approval?.job_id} onChange={setPick} />}
            <ViewerPanel key={`${who}-${url}`} url={url} isTest={!!testGlb} source={ready?.source} sculpt={!testGlb && !!ready?.asset && (!ready.asset.skins || !ready.asset.textures)} refs={st?.reference_views || []} view={view} setView={setView} clipKeys={ready?.clips?.map((c) => c.key) || []} stats={stats} onStats={setStats} name={st?.name} versionLabel={ready ? versionLabel(ready, st.ready_jobs) : ""} prop={prop} />
            {ready && !testGlb && <ApprovalBar st={st} job={ready} onChange={load} />}
          </div>
          <aside className="min-w-0 space-y-6">
            <section className="shadow-soft rounded-[2rem] border border-border/70 bg-card p-4" data-testid="comparison-panel">
              <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground"><Box className="h-4 w-4 text-[#17A2A2]" /> Comparaison · référence officielle</p>
              <div className="grid grid-cols-2 gap-2">
                <figure><img decoding="async" src={st ? refFor(st.reference_views, view) : ""} alt="Référence officielle" className="aspect-[2/3] w-full rounded-2xl bg-[#EEF3F1] object-contain" data-testid="comparison-reference" /><figcaption className="mt-1 text-center text-[11px] font-bold text-muted-foreground">Référence 2D · {VIEW_FR.find((v) => v[0] === view)[1]}</figcaption></figure>
                <figure><img decoding="async" src="/assets/characters/3d/board_b.jpg" alt="Planche officielle de direction artistique" className="aspect-[2/3] w-full rounded-2xl object-cover object-left-top" /><figcaption className="mt-1 text-center text-[11px] font-bold text-muted-foreground">Planche officielle</figcaption></figure>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Choisis une vue : le modèle 3D tourne vers le même angle que la référence.</p>
            </section>
            <GlbUpload character={who} onDone={load} />
            <PipelineCard st={st} running={running} onStarted={load} />
            <QualityPanel stats={stats} isTest={!!testGlb} />
          </aside>
        </div>
      </main>
    </div>
  );
}
